import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';
import { OUTREACH_PITCH_KINDS, type OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';

/** The names of the pitch kinds on the campaign page and the results (design outreach-v2 g1, n1). */
const messages = defineMessages({
  oneSpotifyPlaylist: { id: 'app.outreach.pitchKind.one.spotifyPlaylist', defaultMessage: 'Spotify playlist' },
  oneOtherPlaylist: {
    id: 'app.outreach.pitchKind.one.otherPlaylist',
    defaultMessage: 'Apple Music or Deezer playlist',
  },
  onePress: { id: 'app.outreach.pitchKind.one.press', defaultMessage: 'Blog' },
  oneNewsletter: { id: 'app.outreach.pitchKind.one.newsletter', defaultMessage: 'Newsletter' },
  oneRadio: { id: 'app.outreach.pitchKind.one.radio', defaultMessage: 'Radio show' },
  onePodcast: { id: 'app.outreach.pitchKind.one.podcast', defaultMessage: 'Podcast' },
  oneCreator: { id: 'app.outreach.pitchKind.one.creator', defaultMessage: 'Creator' },
  inSentenceSpotifyPlaylist: {
    id: 'app.outreach.pitchKind.inSentence.spotifyPlaylist',
    defaultMessage: 'Spotify playlist',
  },
  inSentenceOtherPlaylist: {
    id: 'app.outreach.pitchKind.inSentence.otherPlaylist',
    defaultMessage: 'Apple Music or Deezer playlist',
  },
  inSentencePress: { id: 'app.outreach.pitchKind.inSentence.press', defaultMessage: 'music blog' },
  inSentenceNewsletter: { id: 'app.outreach.pitchKind.inSentence.newsletter', defaultMessage: 'newsletter' },
  inSentenceRadio: { id: 'app.outreach.pitchKind.inSentence.radio', defaultMessage: 'radio show' },
  inSentencePodcast: { id: 'app.outreach.pitchKind.inSentence.podcast', defaultMessage: 'podcast' },
  inSentenceCreator: { id: 'app.outreach.pitchKind.inSentence.creator', defaultMessage: 'creator' },
  manySpotifyPlaylist: { id: 'app.outreach.pitchKind.many.spotifyPlaylist', defaultMessage: 'Spotify playlists' },
  manyOtherPlaylist: {
    id: 'app.outreach.pitchKind.many.otherPlaylist',
    defaultMessage: 'Apple Music and Deezer playlists',
  },
  manyPress: { id: 'app.outreach.pitchKind.many.press', defaultMessage: 'Blogs and magazines' },
  manyNewsletter: { id: 'app.outreach.pitchKind.many.newsletter', defaultMessage: 'Newsletters' },
  manyRadio: { id: 'app.outreach.pitchKind.many.radio', defaultMessage: 'Radio shows' },
  manyPodcast: { id: 'app.outreach.pitchKind.many.podcast', defaultMessage: 'Podcasts' },
  manyCreator: { id: 'app.outreach.pitchKind.many.creator', defaultMessage: 'Creators' },
  manyPrivate: { id: 'app.outreach.pitchKind.many.private', defaultMessage: 'Your own contacts' },
});

const ONE: Record<OutreachPitchKind, MessageDescriptor> = {
  spotifyPlaylist: messages.oneSpotifyPlaylist,
  otherPlaylist: messages.oneOtherPlaylist,
  press: messages.onePress,
  newsletter: messages.oneNewsletter,
  radio: messages.oneRadio,
  podcast: messages.onePodcast,
  creator: messages.oneCreator,
};

const MANY: Record<OutreachPitchKind | 'private', MessageDescriptor> = {
  spotifyPlaylist: messages.manySpotifyPlaylist,
  otherPlaylist: messages.manyOtherPlaylist,
  press: messages.manyPress,
  newsletter: messages.manyNewsletter,
  radio: messages.manyRadio,
  podcast: messages.manyPodcast,
  creator: messages.manyCreator,
  private: messages.manyPrivate,
};

export function isPitchKind(value: string | null): value is OutreachPitchKind {
  return OUTREACH_PITCH_KINDS.some((kind) => kind === value);
}

/** "Spotify playlist": the kind of one person, at the start of a line. */
export function pitchKindName(intl: IntlShape, kind: OutreachPitchKind): string {
  return intl.formatMessage(ONE[kind]);
}

/** "Blogs and magazines": a group of a kind. An unknown key shows as it is. */
export function pitchKindGroupName(intl: IntlShape, key: string): string {
  const message = key === 'private' || isPitchKind(key) ? MANY[key] : undefined;
  return message === undefined ? key : intl.formatMessage(message);
}
